/** The quote's line row, its tags and the one quantity input (moved from `quote/quote-rows.tsx` at step 8). */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { type QuoteLineBuying, type QuoteLineView } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { controlClass } from '../../components/form/field.js'
import { MarginCell } from './grid.js'
import { LineShell } from './line-shell.js'
import type { MenuAt } from './add-row.js'
import { KindTile } from '../kind-tile.js'
import { formatRate } from '../../lib/format.js'
import { IconLock, IconWarn } from '../../frame/icons.js'
import { OPTIONAL_LABEL, PRICE_EACH_FOLDED } from '../copy.js'

// ---------------------------------------------------------------------------
// The line
// ---------------------------------------------------------------------------

export interface LineRowProps {
  line: QuoteLineView
  /** Resolves false when the server refused it, and the box puts its number back. */
  onQty: (qty: number) => Promise<boolean> | void
  onOpen: () => void
  /** The ⋮'s point and the ⋮ itself (`MenuAt`). */
  onMenu: (at: MenuAt) => void
  /**
   * A quote that is not a draft (walkthrough findings §4, folder 52): the
   * quantity box disabled, the name opens nothing and the ⋮ is absent.
   */
  readOnly?: boolean
  /** Shown after a re-measure, on the lines it deliberately left alone. */
  showNotRescaled?: boolean
  /**
   * The quantity cell when the adapter's quantity is a rule (`qty: 'rule'`):
   * the base, the rate and what they come to at the sample. Absent, the cell
   * is the number box.
   */
  qtyCell?: ReactNode
}

/**
 * A quote's line, on the shared shell.
 *
 * No `rule` slot: a rule is what a *package* line scales by, and a quote's
 * line has already been scaled. That one absent prop is the whole difference
 * between the two grids.
 */
export function LineRow({
  line, onQty, onOpen, onMenu, readOnly = false, showNotRescaled, qtyCell,
}: LineRowProps) {
  return (
    <>
    <LineShell
      lineId={line.id}
      tile={<KindTile kind={line.kind} size={24} />}
      name={line.description}
      onOpen={readOnly ? undefined : onOpen}
      tags={(
        <>
          {line.priceEdited && <LineTag tone="warn">edited</LineTag>}
          {line.optional && <LineTag tone="info">{OPTIONAL_LABEL}</LineTag>}
          {showNotRescaled && <LineTag tone="warn">not rescaled</LineTag>}
        </>
      )}
      sub={line.note || undefined}
      qty={qtyCell ?? (
        <QtyInput
          value={line.qty}
          onCommit={onQty}
          disabled={readOnly}
          label={`${line.description} quantity`}
          /*
           * No height of its own. `h-[30px]` here made a quote's line row
           * 45px where a package's identical row was 51, which is the six
           * pixels Aaron could see between the two screens. The kit's 36 is
           * the height, on both.
           */
          className="justify-end"
        />
      )}
      /*
       * The unit alone. Labor also printed "· 2.5 h" here, which is the
       * quantity again — a labor line's quantity *is* its hours — so the row
       * said the same number twice.
       */
      unit={<span className="block truncate text-[12.5px] text-text-muted" title={line.unit}>{line.unit}</span>}
      price={(
        <span className="flex flex-col items-end gap-px text-right">
          <span className="tnum">{formatRate(line.rate)}</span>
          {line.priceEdited && line.standardRate !== line.rate && (
            <span className="tnum text-[10.5px] text-text-faint line-through">
              {formatRate(line.standardRate)}
            </span>
          )}
        </span>
      )}
      /* Narrower than 860 the card folds Price each under the name (§5.5). */
      priceFolded={(
        <>
          {PRICE_EACH_FOLDED(formatRate(line.rate))}
          {line.priceEdited && line.standardRate !== line.rate && (
            <span className="ml-1 text-text-faint line-through">{formatRate(line.standardRate)}</span>
          )}
        </>
      )}
      /*
       * What this line costs us at this quantity — read-only, and the number
       * the service's cost above it is the sum of. An optional line's cost
       * is shown but not summed until somebody takes it, the same as its
       * price.
       */
      cost={(
        <span className={cn('tnum text-right text-[13px]',
          line.optional ? 'text-text-faint' : 'text-text-muted')}
          title={line.optional ? 'Not in the service cost until the customer adds it' : undefined}>
          {formatRate(line.cost)}
        </span>
      )}
      margin={<MarginCell pct={line.marginPct} />}
      total={<b className="tnum text-right text-[13.5px] font-bold text-text">{formatRate(line.amount)}</b>}
      onMenu={readOnly ? undefined : onMenu}
    />
    {line.buying?.text && <BuyingLine buying={line.buying} />}
    </>
  )
}

/**
 * **How it is bought**, under the line (catalog contract §8, Board 2): the
 * tinted internal row with a lock, or a warning where buying the whole one
 * costs the margin — `Buy 3 skids (1,560 sq ft) · 360 sq ft back to stock`,
 * `Hired 1 week · 2 days idle · paid in full`. The server wrote the sentence
 * and the tone; this only draws them. Never on the proposal: `ProposalView`
 * does not map `buying`.
 */
export function BuyingLine({ buying, phone }: { buying: QuoteLineBuying; phone?: boolean }) {
  const loud = buying.tone !== 'plain'
  return (
    <div
      data-buying-line={buying.tone}
      className={cn('tnum flex items-start gap-1.5 border-t border-dashed border-line-soft',
        'bg-surface-internal text-[12px] leading-[1.4]',
        phone ? 'px-3.5 py-2' : 'py-[7px] pl-[66px] pr-3.5',
        buying.tone === 'bad' ? 'text-danger' : buying.tone === 'warn' ? 'text-warn' : 'text-text-muted')}
    >
      <span aria-hidden className="mt-px shrink-0">
        {loud ? <IconWarn size={13} /> : <IconLock size={11} />}
      </span>
      <span className="min-w-0">{buying.text}</span>
    </div>
  )
}

export function LineTag({ children, tone }: { children: ReactNode; tone: 'warn' | 'info' }) {
  return (
    <span
      className={cn(
        'inline-flex h-[18px] shrink-0 items-center whitespace-nowrap rounded-tag px-1.5',
        'text-[10.5px] font-semibold',
        tone === 'warn' ? 'bg-warn-bg text-warn' : 'bg-info-bg text-info',
      )}
    >
      {children}
    </span>
  )
}

// ---------------------------------------------------------------------------
// The one input
// ---------------------------------------------------------------------------

/**
 * A quantity, committed on blur or Enter and abandoned on Escape.
 *
 * Not on every keystroke: each commit is a round trip that recomputes the
 * whole quote, and typing "12.5" would fire four of them — one of which asks
 * the server what a quantity of 1 costs. Blur-and-Enter is also what a
 * spreadsheet does, which is the thing people think they are using.
 *
 * `inputMode="decimal"` opens the numeric keypad on a phone (§7). `step` is 1
 * for a visit count and free for a line, because 2.5 visits is not a thing and
 * 2.5 tons is.
 */
/**
 * The add links' words: plural, because each adds one of many.
 *
 * `QUOTE_LINE_KIND_LABELS` names a *line* — "Material", "Fee" — and that is
 * right on a row and wrong on a link. Kept here beside the only place that
 * needs them rather than in `shared`, because a service's recipe imports this
 * module for `QtyInput` anyway and a third vocabulary is the thing being
 * avoided.
 */
export const ADD_LINK_LABELS: Record<'item' | 'labor' | 'equipment' | 'deployment', string> = {
  item: 'Materials',
  labor: 'Labor',
  equipment: 'Equipment',
  deployment: 'Fees',
}

export function QtyInput({
  value, onCommit, label, className, width, step, unit, disabled = false,
}: {
  value: number
  /** May resolve false — the server refused it — and then the box shows `value` again. */
  onCommit: (value: number) => Promise<boolean> | boolean | void
  label: string
  className?: string
  /**
   * How wide, as one class — `w-full` unless said otherwise.
   *
   * Separate from `className` because a width appended there fights the one
   * `controlClass` emits and loses: every `w-[68px]` on this control was
   * being drawn at `w-full`. See the note on `controlClass`'s `width`.
   */
  width?: string
  step?: number
  /**
   * Printed inside the box, small and muted — the phone's arrangement (§7).
   * On a phone the unit cannot have a column of its own, and a quantity with
   * no unit beside it is a number nobody can check.
   */
  unit?: string
  /** A quote that is not a draft: the number is shown, and cannot be typed over. */
  disabled?: boolean
}) {
  /* NaN is "nothing set yet", drawn as an empty box rather than the word. */
  const said = (number: number) => (Number.isNaN(number) ? '' : String(number))
  const [draft, setDraft] = useState(() => said(value))
  const box = useRef<HTMLInputElement>(null)
  const latest = useRef(value)
  latest.current = value

  // Follow the value when it changes underneath — a re-measure, an undo — but
  // never while somebody is typing into it.
  useEffect(() => {
    if (document.activeElement === box.current) return
    setDraft(said(value))
  }, [value])

  /*
   * Zero is an answer.
   *
   * This used to refuse anything `<= 0` and silently put the old number back,
   * so a base quantity of nought — a line that only scales, with no fixed
   * part — could not be typed at all, and the box lied about having taken
   * what was typed into it. Only a negative quantity and a non-number are
   * refused now; both are things no line can mean.
   */
  const commit = () => {
    const text = draft.trim()
    if (text === '') { setDraft(said(value)); return }
    const next = Number(text)
    if (!Number.isFinite(next) || next < 0) { setDraft(said(value)); return }
    if (next === value) { setDraft(said(value)); return }
    /*
     * A refusal puts the old number back. Otherwise the box goes on showing
     * what was typed over a line whose quantity — and cost — never moved,
     * which is how a 0 sat in front of a cost nobody could account for.
     */
    void Promise.resolve(onCommit(step === 1 ? Math.round(next) : next)).then((ok) => {
      if (ok === false && document.activeElement !== box.current) setDraft(said(latest.current))
    })
  }

  const field = (
    <input
      ref={box}
      value={draft}
      aria-label={label}
      inputMode="decimal"
      disabled={disabled}
      data-qty
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') { event.currentTarget.blur() }
        if (event.key === 'Escape') { setDraft(said(value)); event.currentTarget.blur() }
      }}
      /*
       * The same box every other field in the product wears. It used to carry
       * its own radius, border, font and — through each caller's `className`
       * — its own height, which is how one builder row ended up with a 24px
       * dropdown beside a 38px number.
       */
      className={cn(
        /* Inside a unit box the input is the whole of it and wears no chrome. */
        controlClass({ width: unit ? 'w-full' : width }),
        'text-right',
        /*
         * `!px-0`, not `px-0`: `cn` concatenates rather than merges, and the
         * control's own `px-[11px]` sits later in the stylesheet — so the
         * input kept 22px of padding inside an 80px box and drew "5.17" as
         * "5" (found on the phone row at 390).
         */
        unit && 'border-0 bg-transparent !px-0 focus-within:ring-0 focus:ring-0',
        className,
      )}
    />
  )

  if (!unit) return field

  return (
    <span className={cn(controlClass({ width }), 'shrink-0 gap-1', className)}>
      {field}
      <span className="shrink-0 text-[11px] font-medium text-text-muted">{unit}</span>
    </span>
  )
}
